import { Search24Regular } from "@fluentui/react-icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import {
  globalSearch,
  type SearchHit,
  type SearchHitType,
} from "../search/searchApi";
import { getSearchHitPath } from "../search/searchNavigation";

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;

const typeOrder: SearchHitType[] = [
  "Cliente",
  "Fornecedor",
  "Subempreiteiro",
  "Obra",
  "Fatura",
];

const typeGroupLabels: Record<SearchHitType, string> = {
  Cliente: "Clientes",
  Fornecedor: "Fornecedores",
  Subempreiteiro: "Subempreiteiros",
  Obra: "Obras",
  Fatura: "Faturas",
};

export function GlobalSearchBar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Equipa interna (papel TeamMember): a pesquisa global cobre áreas de back-office
  // que o backend lhe nega — escondê-la evita uma caixa que só devolve erros.
  const isTeamMemberOnly =
    (user?.roles?.includes("TeamMember") ?? false) &&
    !(user?.roles?.includes("Administrator") ?? false) &&
    !(user?.roles?.includes("User") ?? false);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchHit[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const trimmedQuery = query.trim();
  const hasActiveQuery = trimmedQuery.length >= MIN_QUERY_LENGTH;

  // Debounced search with cancellation of stale requests.
  useEffect(() => {
    if (!hasActiveQuery) {
      setResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);

    let cancelled = false;
    const timer = setTimeout(() => {
      globalSearch(trimmedQuery)
        .then((result) => {
          if (cancelled) {
            return;
          }
          setResults(result.hits ?? []);
        })
        .catch(() => {
          if (cancelled) {
            return;
          }
          setResults([]);
        })
        .finally(() => {
          if (cancelled) {
            return;
          }
          setIsSearching(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [trimmedQuery, hasActiveQuery]);

  // Close dropdown on click outside.
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleMouseDown = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [isOpen]);

  const groupedResults = useMemo(() => {
    const groups = results.reduce(
      (acc, hit) => {
        (acc[hit.type] ??= []).push(hit);
        return acc;
      },
      {} as Record<string, SearchHit[]>,
    );

    return typeOrder
      .filter((type) => groups[type]?.length)
      .map((type) => [type, groups[type]] as [SearchHitType, SearchHit[]]);
  }, [results]);

  const closeAndReset = () => {
    setIsOpen(false);
    setQuery("");
    inputRef.current?.blur();
  };

  const handleSelect = (hit: SearchHit) => {
    navigate(getSearchHitPath(hit));
    closeAndReset();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  const showDropdown = isOpen && hasActiveQuery;

  if (isTeamMemberOnly) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full hidden md:block"
      style={{ maxWidth: "480px" }}
    >
      <Search24Regular className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none w-5 h-5" />
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder="Pesquisar..."
        aria-label="Pesquisar em toda a aplicação"
        className="w-full h-8 pl-10 pr-3 bg-input-background border border-border text-foreground placeholder:text-[var(--placeholder-text)] outline-none transition-colors focus:border-[var(--ring)]"
        style={{ borderRadius: "var(--radius-button)" }}
      />

      {showDropdown && (
        <div
          className="absolute left-0 top-full mt-2 w-full bg-card border border-border shadow-lg z-50 overflow-y-auto"
          style={{
            borderRadius: "var(--radius-card)",
            minWidth: "320px",
            maxHeight: "70vh",
          }}
        >
          {isSearching ? (
            <div className="px-4 py-2 text-sm text-muted-foreground">
              A pesquisar...
            </div>
          ) : groupedResults.length === 0 ? (
            <div className="px-4 py-2 text-sm text-muted-foreground">
              Sem resultados
            </div>
          ) : (
            <div className="py-2">
              {groupedResults.map(([type, hits]) => (
                <div key={type}>
                  <div
                    className="px-4 py-1 text-xs uppercase text-muted-foreground"
                    style={{ fontWeight: "var(--font-weight-semi-bold)" }}
                  >
                    {typeGroupLabels[type]}
                  </div>
                  {hits.map((hit) => (
                    <button
                      key={`${hit.type}-${hit.id}`}
                      type="button"
                      onClick={() => handleSelect(hit)}
                      className="w-full px-4 py-2 text-left hover:bg-accent transition-colors"
                    >
                      <div className="text-foreground truncate">
                        {hit.title}
                      </div>
                      {hit.subtitle && (
                        <div className="text-sm text-muted-foreground truncate">
                          {hit.subtitle}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
